/**
 * BotSpot Web Dashboard API Types
 *
 * TypeScript definitions matching backend API schemas.
 */

/**
 * Bot Types
 */
export enum BotType {
  MOLTBOT = "moltbot",
  OPENCLAW = "openclaw",
  PICOCLAW = "picoclaw",
  PICOBOT = "picobot",
  NANOBOT = "nanobot",
  CUSTOM = "custom",
}

/**
 * Bot Status
 */
export enum BotStatus {
  CREATING = "creating",
  HATCHING = "hatching",
  CONFIGURING = "configuring",
  STARTING = "starting",
  RUNNING = "running",
  STOPPING = "stopping",
  STOPPED = "stopped",
  PAUSED = "paused",
  FAILED = "failed",
  DESTROYED = "destroyed",
  TIMED_OUT = "timed_out",
}

/**
 * Resource Configuration
 */
export interface ResourceConfig {
  vcpus: number;
  memory_mb: number;
}

/**
 * Skill Installation
 */
export interface SkillInstall {
  name: string;
  version?: string;
  config?: Record<string, unknown>;
}

/**
 * Bot Public Schema
 */
export interface Bot {
  id: string;
  user_id: string;
  name: string;
  bot_type: BotType;
  status: BotStatus;
  resources: ResourceConfig;
  skills: SkillInstall[];
  error_message?: string;
  last_active_at?: string;
  started_at?: string;
  stopped_at?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Bot Creation Request
 */
export interface BotCreate {
  name: string;
  bot_type: string;
  vcpus?: number;
  memory_mb?: number;
  skills?: string[];
  credential_ids?: string[];
}

/**
 * Bot Update Request
 */
export interface BotUpdate {
  name?: string;
  resources?: ResourceConfig;
  skills?: SkillInstall[];
}

/**
 * Bot List Response
 */
export interface BotList {
  items: Bot[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Bot Health Status
 */
export interface BotHealth {
  bot_id: string;
  status: BotStatus;
  healthy: boolean;
  uptime_seconds?: number;
  cpu_percent?: number;
  memory_percent?: number;
  error_message?: string;
  last_check_at: string;
}

/**
 * Message Response
 */
export interface MessageResponse {
  message: string;
}

/**
 * Authentication Types
 */

/**
 * Login Request
 */
export interface LoginRequest {
  username: string;
  password: string;
}

/**
 * Token Response
 */
export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * User Registration Request
 */
export interface RegisterRequest {
  email: string;
  password: string;
  full_name?: string;
}

/**
 * User Public Schema
 */
export interface User {
  id: string;
  username: string;
  email: string;
  created_at: string;
  updated_at: string;
}

/**
 * Error Response
 */
export interface ErrorResponse {
  detail: string | Record<string, unknown>;
}

/**
 * Pagination Parameters
 */
export interface PaginationParams {
  page?: number;
  page_size?: number;
}

/**
 * Bot Metrics Types
 */

export interface CPUMetrics {
  usage_percent: number;
  vcpus: number;
}

export interface MemoryMetrics {
  usage_mb: number;
  total_mb: number;
  usage_percent: number;
}

export interface NetworkMetrics {
  rx_bytes: number;
  tx_bytes: number;
  rx_packets: number;
  tx_packets: number;
}

export interface DiskMetrics {
  read_bytes: number;
  write_bytes: number;
  read_ops: number;
  write_ops: number;
}

export interface BotMetrics {
  bot_id: string;
  bot_name: string;
  status: string;
  uptime_seconds: number | null;
  cpu: CPUMetrics;
  memory: MemoryMetrics;
  network: NetworkMetrics;
  disk: DiskMetrics;
  collected_at: string;
}

/**
 * Bot Logs Types
 */

export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
  CRITICAL = "critical",
}

export interface LogEntry {
  id: string;
  bot_id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  source?: string;
  metadata?: Record<string, unknown>;
}

export interface LogList {
  items: LogEntry[];
  total: number;
  page: number;
  page_size: number;
  bot_id: string;
}

/**
 * Chat Types
 */

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

export interface SendMessageRequest {
  message: string;
}

export interface ChatResponse {
  response: string;
  exit_code: number;
}

/**
 * Credential Types
 */

export enum CredentialProvider {
  CLAUDE = "claude",
  OPENAI = "openai",
  DISCORD = "discord",
  TELEGRAM = "telegram",
  SLACK = "slack",
  GITHUB = "github",
  CUSTOM = "custom",
}

export interface Credential {
  id: string;
  user_id: string;
  provider: CredentialProvider;
  name: string;
  is_active: boolean;
  is_expired: boolean;
  expires_at?: string;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CredentialCreate {
  provider: CredentialProvider;
  name: string;
  credential_value: string;
  expires_at?: string;
}

export interface CredentialUpdate {
  name?: string;
  credential_value?: string;
  expires_at?: string;
  is_active?: boolean;
}

export interface CredentialList {
  credentials: Credential[];
  total: number;
}

/**
 * Hatchery Types
 */

/**
 * MBTI Profile
 */
export interface MBTIProfile {
  ei: number;
  sn: number;
  tf: number;
  jp: number;
}

/**
 * Persona Profile
 */
export interface PersonaProfile {
  name: string;
  gender_expression: string;
  appearance: string;
  voice: string;
  psychology: string;
  strengths: string[];
  narrative: string;
  mbti_type?: string;
}

/**
 * SOUL Profile
 */
export interface SOULProfile {
  mbti: MBTIProfile;
  traits?: Record<string, unknown>;
}

/**
 * Demo Hatch Request
 */
export interface DemoHatchRequest {
  creator_name: string;
}

/**
 * Hatch Response
 */
export interface HatchResponse {
  agent_id: string;
  public_key: string;
  public_key_fingerprint: string;
  private_key_pem: string;
  soul: SOULProfile;
  soul_hash: string;
  block_index: number;
  block_hash: string;
  persona?: PersonaProfile;
}

/**
 * Block Public Data
 */
export interface BlockPublic {
  index: number;
  timestamp: string;
  previous_hash: string;
  agent_id: string;
  public_key_fingerprint: string;
  soul_hash: string;
  nonce: number;
  difficulty: number;
  block_hash: string;
}

/**
 * Agent Identity Public Data
 */
export interface AgentIdentityPublic {
  agent_id: string;
  public_key: string;
  public_key_fingerprint: string;
  soul: SOULProfile;
  soul_hash: string;
  block_index: number;
  creator_id: string;
  platform: string;
  hatched_at: string;
  persona?: PersonaProfile;
  block?: BlockPublic;
}

/**
 * Gallery Response
 */
export interface GalleryResponse {
  agents: AgentIdentityPublic[];
  total: number;
}
